import { z } from 'zod';
import { CrewClaimResolutionMethodEnum, CrewIncentiveTypeEnum, InventoryBehavior, } from '../../enums/crew.js';
import { mapped } from '../../typing.js';
import { enumToZodLiteralUnion, helperSchema, optionalize, } from '../../zodExtensions.js';
// eslint-disable-next-line @typescript-eslint/no-namespace
export var Pegasus;
(function (Pegasus) {
    let Operator;
    (function (Operator) {
        Operator["equal"] = "equal";
        Operator["notEqual"] = "notEqual";
        Operator["lessThan"] = "lessThan";
        Operator["greaterThan"] = "greaterThan";
        Operator["in"] = "in";
        Operator["notIn"] = "notIn";
        Operator["contains"] = "contains";
        Operator["doesNotContain"] = "doesNotContain";
    })(Operator = Pegasus.Operator || (Pegasus.Operator = {}));
    // ? possibly `concept/integration/data`
    let Fact;
    (function (Fact) {
        // order lookup
        Fact["orderDaysSinceCreated"] = "order/daysSinceCreated";
        Fact["statusCustomer"] = "status/customer";
        // ? the channel the registration was made on (eg, Costco, Amazon, etc)
        Fact["registrationChannel"] = "registration/channel";
        Fact["registrationEstimatedPurchaseDate"] = "registration/estimatedPurchaseDate";
        Fact["registrationDaysSincePurchaseEstimate"] = "registration/daysSincePurchaseEstimate";
        Fact["orderLineItemCompareAtPricePercentage"] = "order/lineItem/compareAtPricePercentage";
        Fact["orderDiscountCode"] = "order/discountCode";
        Fact["orderFinancialStatus"] = "order/financialStatus";
        Fact["orderCreated"] = "order/created";
        Fact["orderChannel"] = "order/channel";
        Fact["orderGateway"] = "order/gateway";
        Fact["orderCountry"] = "order/country";
        Fact["orderProvince"] = "order/province";
        Fact["orderTotal"] = "order/total";
        Fact["orderTags"] = "order/tags";
        Fact["orderCanceled"] = "order/canceled";
        Fact["orderFulfillmentDisplayStatus"] = "order/fulfillmentDisplayStatus";
        Fact["orderProtectedByCorso"] = "order/protectedByCorso";
        Fact["orderFulfillmentLocationId"] = "order/fulfillmentLocationId";
        Fact["orderNumber"] = "order/number";
        Fact["customerSegmentId"] = "customer/segmentId";
        Fact["customerTags"] = "customer/tags";
        Fact["customerEmail"] = "customer/email";
        Fact["customerTotalReturnCountLastYear"] = "customer/totalReturnCount/lastYear";
        Fact["customerTotalOrderCountAllTime"] = "customer/totalOrderCount/allTime";
        Fact["exchangeOrderTotal"] = "exchangeOrder/total";
        // after reason selection
        Fact["productVendor"] = "product/vendor";
        Fact["productTags"] = "product/tags";
        Fact["productTypes"] = "product/types";
        Fact["productCollections"] = "product/collections";
        Fact["productSku"] = "product/sku";
        Fact["cartDeliveryGroupType"] = "cart/deliveryGroupType";
        Fact["productGroupId"] = "product/groupId";
        Fact["reasonId"] = "reason/id";
        Fact["reasonCategoryCode"] = "reason/categoryCode";
        Fact["reasonDetailId"] = "reason/detailId";
        Fact["claimType"] = "claim/type";
        Fact["claimTotal"] = "claim/total";
        Fact["claimLineItemRequestedResolutionMethod"] = "claimLineItem/requestedResolutionMethod";
        Fact["claimLineItemTotal"] = "claimLineItem/total";
        Fact["claimLineItemTotalQuantity"] = "claimLineItem/totalQuantity";
        Fact["claimLineItemCustomFieldResponse"] = "claimLineItem/customFieldResponse";
        Fact["claimLineItemInspectionGrade"] = "claimLineItem/inspectionGrade";
        // claim created
        Fact["rmaStatus"] = "rma/status";
        Fact["rmaAllItemsReceived"] = "rma/allItemsReceived";
        Fact["returnShipmentStatus"] = "returnShipment/status";
        Fact["returnShipmentLocationId"] = "returnShipment/locationId";
        Fact["intelligemsTestGroupId"] = "intelligems/testGroupId";
    })(Fact = Pegasus.Fact || (Pegasus.Fact = {}));
    let ValueType;
    (function (ValueType) {
        ValueType["numberArray"] = "numberArray";
        ValueType["stringArray"] = "stringArray";
        ValueType["number"] = "number";
        ValueType["boolean"] = "boolean";
        ValueType["date"] = "date";
    })(ValueType = Pegasus.ValueType || (Pegasus.ValueType = {}));
    Pegasus.valueTypeSchema = {
        [ValueType.stringArray]: z.array(z.string()),
        [ValueType.numberArray]: z.array(z.number()),
        [ValueType.number]: z.number(),
        [ValueType.boolean]: z.boolean(),
        [ValueType.date]: z.string().datetime(),
    };
    Pegasus.valueOperators = {
        [ValueType.numberArray]: [Operator.in, Operator.notIn],
        [ValueType.stringArray]: [
            Operator.in,
            Operator.notIn,
            Operator.contains,
            Operator.doesNotContain,
        ],
        [ValueType.number]: [Operator.lessThan, Operator.greaterThan],
        [ValueType.boolean]: [Operator.equal, Operator.notEqual],
        [ValueType.date]: [Operator.lessThan, Operator.greaterThan],
    };
    const arrayValueTypes = Object.values({
        [ValueType.stringArray]: ValueType.stringArray,
        [ValueType.numberArray]: ValueType.numberArray,
    });
    function isArrayValueType(valueType) {
        const compare = arrayValueTypes; // widen type for comparison; otherwise, TypeScript already expects they're different
        return compare.includes(valueType);
    }
    Pegasus.isArrayValueType = isArrayValueType;
    Pegasus.factValueType = {
        [Fact.orderCreated]: ValueType.date,
        [Fact.orderCountry]: ValueType.stringArray,
        [Fact.orderTotal]: ValueType.number,
        [Fact.orderTags]: ValueType.stringArray,
        [Fact.customerTags]: ValueType.stringArray,
        [Fact.rmaStatus]: ValueType.stringArray,
        [Fact.rmaAllItemsReceived]: ValueType.boolean,
        [Fact.returnShipmentStatus]: ValueType.stringArray,
        [Fact.productTags]: ValueType.stringArray,
        [Fact.productTypes]: ValueType.stringArray,
        [Fact.reasonId]: ValueType.numberArray,
        [Fact.reasonCategoryCode]: ValueType.stringArray,
        [Fact.reasonDetailId]: ValueType.numberArray,
        [Fact.claimType]: ValueType.stringArray,
        [Fact.orderNumber]: ValueType.stringArray,
        [Fact.customerEmail]: ValueType.stringArray,
        [Fact.orderChannel]: ValueType.stringArray,
        [Fact.orderProtectedByCorso]: ValueType.boolean,
        [Fact.claimLineItemRequestedResolutionMethod]: ValueType.stringArray,
        [Fact.productCollections]: ValueType.stringArray,
        [Fact.exchangeOrderTotal]: ValueType.number,
        [Fact.claimLineItemTotal]: ValueType.number,
        [Fact.claimLineItemCustomFieldResponse]: ValueType.stringArray,
        [Fact.orderDaysSinceCreated]: ValueType.number,
        [Fact.statusCustomer]: ValueType.boolean,
        [Fact.orderGateway]: ValueType.stringArray,
        [Fact.registrationChannel]: ValueType.stringArray,
        [Fact.registrationEstimatedPurchaseDate]: ValueType.date,
        [Fact.orderDiscountCode]: ValueType.stringArray,
        [Fact.claimTotal]: ValueType.number,
        [Fact.productVendor]: ValueType.stringArray,
        [Fact.productSku]: ValueType.stringArray,
        [Fact.productGroupId]: ValueType.numberArray,
        [Fact.customerTotalReturnCountLastYear]: ValueType.number,
        [Fact.customerTotalOrderCountAllTime]: ValueType.number,
        [Fact.orderProvince]: ValueType.stringArray,
        [Fact.orderFulfillmentLocationId]: ValueType.numberArray,
        [Fact.cartDeliveryGroupType]: ValueType.stringArray,
        [Fact.orderLineItemCompareAtPricePercentage]: ValueType.number,
        [Fact.intelligemsTestGroupId]: ValueType.stringArray,
        [Fact.customerSegmentId]: ValueType.numberArray,
        [Fact.orderFulfillmentDisplayStatus]: ValueType.stringArray,
        [Fact.orderCanceled]: ValueType.boolean,
        [Fact.orderFinancialStatus]: ValueType.stringArray,
        [Fact.registrationDaysSincePurchaseEstimate]: ValueType.number,
        [Fact.claimLineItemInspectionGrade]: ValueType.stringArray,
        [Fact.returnShipmentLocationId]: ValueType.numberArray,
        [Fact.claimLineItemTotalQuantity]: ValueType.number,
    };
    /** Defines the expression for a fact with the operators, and value of it's associated value type. */
    const expression = (fact) => {
        const valueType = Pegasus.factValueType[fact];
        return z.object({
            fact: z.literal(`${fact}`),
            operator: z.union(mapped(Pegasus.valueOperators[valueType], (operator) => z.literal(`${operator}`))),
            value: Pegasus.valueTypeSchema[valueType],
        });
    };
    /** A utility value to use the array facts that are real. */
    Pegasus.arrayFacts = Object.values({
        [Fact.orderCountry]: Fact.orderCountry,
        [Fact.orderTags]: Fact.orderTags,
        [Fact.customerTags]: Fact.customerTags,
        [Fact.productTags]: Fact.productTags,
        [Fact.productTypes]: Fact.productTypes,
        [Fact.reasonId]: Fact.reasonId,
        [Fact.reasonCategoryCode]: Fact.reasonCategoryCode,
        [Fact.reasonDetailId]: Fact.reasonDetailId,
        [Fact.claimType]: Fact.claimType,
        [Fact.rmaStatus]: Fact.rmaStatus,
        [Fact.returnShipmentStatus]: Fact.returnShipmentStatus,
        [Fact.orderNumber]: Fact.orderNumber,
        [Fact.customerEmail]: Fact.customerEmail,
        [Fact.orderChannel]: Fact.orderChannel,
        [Fact.productCollections]: Fact.productCollections,
        [Fact.claimLineItemRequestedResolutionMethod]: Fact.claimLineItemRequestedResolutionMethod,
        [Fact.orderGateway]: Fact.orderGateway,
        [Fact.claimLineItemCustomFieldResponse]: Fact.claimLineItemCustomFieldResponse,
        [Fact.registrationChannel]: Fact.registrationChannel,
        [Fact.orderDiscountCode]: Fact.orderDiscountCode,
        [Fact.productVendor]: Fact.productVendor,
        [Fact.productSku]: Fact.productSku,
        [Fact.productGroupId]: Fact.productGroupId,
        [Fact.orderProvince]: Fact.orderProvince,
        [Fact.orderFulfillmentLocationId]: Fact.orderFulfillmentLocationId,
        [Fact.intelligemsTestGroupId]: Fact.intelligemsTestGroupId,
        [Fact.cartDeliveryGroupType]: Fact.cartDeliveryGroupType,
        [Fact.customerSegmentId]: Fact.customerSegmentId,
        [Fact.orderFulfillmentDisplayStatus]: Fact.orderFulfillmentDisplayStatus,
        [Fact.orderFinancialStatus]: Fact.orderFinancialStatus,
        [Fact.claimLineItemInspectionGrade]: Fact.claimLineItemInspectionGrade,
        [Fact.returnShipmentLocationId]: Fact.returnShipmentLocationId,
    });
    function isArrayFact(fact) {
        const compare = Pegasus.arrayFacts; // widen type for comparison; otherwise, TypeScript already expects they're different
        return compare.includes(fact);
    }
    Pegasus.isArrayFact = isArrayFact;
    let ArrayFactType;
    (function (ArrayFactType) {
        /**
         * Represents that the underlying value type is represented by any value of that type.
         * Arbitrary values are expected to be self-representing, so they are their own label at runtime.
         */
        ArrayFactType["arbitrary"] = "arbitrary";
        /**
         * Represents that the underlying value type is represented by a strict list of options.
         * This is often useful for facts that are primary keys, or other values that are strictly defined.
         * Any definite value is expected to have a `string` label associated with it at runtime.
         * These are enforced by a separate schema for the value.
         * @see `arrayFactDataSchema`
         */
        ArrayFactType["definite"] = "definite";
    })(ArrayFactType = Pegasus.ArrayFactType || (Pegasus.ArrayFactType = {}));
    Pegasus.arrayFactType = {
        [Fact.orderCountry]: ArrayFactType.definite,
        [Fact.orderTags]: ArrayFactType.arbitrary,
        [Fact.customerTags]: ArrayFactType.arbitrary,
        [Fact.rmaStatus]: ArrayFactType.arbitrary,
        [Fact.returnShipmentStatus]: ArrayFactType.definite,
        [Fact.productTags]: ArrayFactType.arbitrary,
        [Fact.productTypes]: ArrayFactType.arbitrary,
        [Fact.reasonId]: ArrayFactType.definite,
        [Fact.reasonCategoryCode]: ArrayFactType.definite,
        [Fact.reasonDetailId]: ArrayFactType.definite,
        [Fact.claimType]: ArrayFactType.definite,
        [Fact.orderNumber]: ArrayFactType.arbitrary,
        [Fact.customerEmail]: ArrayFactType.arbitrary,
        [Fact.orderChannel]: ArrayFactType.definite,
        [Fact.claimLineItemRequestedResolutionMethod]: ArrayFactType.definite,
        [Fact.productCollections]: ArrayFactType.arbitrary,
        [Fact.claimLineItemCustomFieldResponse]: ArrayFactType.definite,
        [Fact.orderGateway]: ArrayFactType.definite,
        [Fact.registrationChannel]: ArrayFactType.definite,
        [Fact.orderDiscountCode]: ArrayFactType.arbitrary,
        [Fact.productVendor]: ArrayFactType.arbitrary,
        [Fact.productSku]: ArrayFactType.arbitrary,
        [Fact.productGroupId]: ArrayFactType.definite,
        [Fact.orderProvince]: ArrayFactType.definite,
        [Fact.orderFulfillmentLocationId]: ArrayFactType.definite,
        [Fact.intelligemsTestGroupId]: ArrayFactType.arbitrary,
        [Fact.cartDeliveryGroupType]: ArrayFactType.definite,
        [Fact.customerSegmentId]: ArrayFactType.definite,
        [Fact.orderFulfillmentDisplayStatus]: ArrayFactType.definite,
        [Fact.orderFinancialStatus]: ArrayFactType.definite,
        [Fact.claimLineItemInspectionGrade]: ArrayFactType.definite,
        [Fact.returnShipmentLocationId]: ArrayFactType.definite,
    };
    function isArbitraryFact(fact) {
        if (!isArrayFact(fact))
            return false;
        return Pegasus.arrayFactType[fact] === ArrayFactType.arbitrary;
    }
    Pegasus.isArbitraryFact = isArbitraryFact;
    const arbitraryValueTypes = Object.values({
        [ValueType.stringArray]: ValueType.stringArray,
    });
    function isArbitraryValueType(valueType) {
        const compare = arbitraryValueTypes; // widen type for comparison; otherwise, TypeScript already expects they're different
        return compare.includes(valueType);
    }
    Pegasus.isArbitraryValueType = isArbitraryValueType;
    function isDefiniteFact(fact) {
        if (!isArrayFact(fact))
            return false;
        return Pegasus.arrayFactType[fact] === ArrayFactType.definite;
    }
    Pegasus.isDefiniteFact = isDefiniteFact;
    const definiteValueTypes = Object.values({
        [ValueType.stringArray]: ValueType.stringArray,
        [ValueType.numberArray]: ValueType.numberArray,
    });
    function isDefiniteValueType(valueType) {
        const compare = definiteValueTypes; // widen type for comparison; otherwise, TypeScript already expects they're different
        return compare.includes(valueType);
    }
    Pegasus.isDefiniteValueType = isDefiniteValueType;
    Pegasus.EventType = {
        modifyResolutionWindow: 'modifyResolutionWindow',
        autoFinalizeClaim: 'autoFinalizeClaim',
        modifyIncentives: 'modifyIncentives',
        applyFee: 'applyFee',
        askAPolicyEnforcingQuestion: 'askAPolicyEnforcingQuestion',
        offerInstantExchange: 'offerInstantExchange',
        offerAdvancedExchange: 'offerAdvancedExchange',
        allowClaimTypeSelection: 'allowClaimTypeSelection',
        collectCustomFields: 'collectCustomFields',
        applyClaimTags: 'applyClaimTags',
        customizeClaimFinalizationSettings: 'customizeClaimFinalizationSettings',
        chargeForReturnLabel: 'chargeForReturnLabel',
        chargeForExchangeOrderShipping: 'chargeForExchangeOrderShipping',
        allowCustomerToKeepItem: 'allowCustomerToKeepItem',
        productRegistrationSelection: 'productRegistrationSelection',
        returnShippingConfig: 'returnShippingConfig',
        applyOrderTags: 'applyOrderTags',
        customizeClaimLineItemInspection: 'customizeClaimLineItemInspection',
        quoteOrderShipment: 'quoteOrderShipment',
        quoteOrderShipmentModify: 'quoteOrderShipmentModify',
        quoteOrderShipmentParcel: 'quoteOrderShipmentParcel',
        quoteOrderShipmentDeliveryEstimate: 'quoteOrderShipmentDeliveryEstimate',
        themeAppExtensionLoad: 'themeAppExtensionLoad',
        orderEditing: 'orderEditing',
        restockInventory: 'restockInventory',
        shopNow: 'shopNow',
    };
    const contextualUsageOnlyEvents = [
        Pegasus.EventType.quoteOrderShipment,
        Pegasus.EventType.quoteOrderShipmentModify,
        Pegasus.EventType.quoteOrderShipmentParcel,
        Pegasus.EventType.quoteOrderShipmentDeliveryEstimate,
    ];
    // TODO: remove this when we are ready to go live
    const unreleasedEvents = [Pegasus.EventType.shopNow];
    // TODO evaluate how events are hidden, and support dynamically hiding events and facts based on what's enabled
    Pegasus.hiddenEvents = [
        ...new Set([...contextualUsageOnlyEvents, ...unreleasedEvents]),
    ];
    Pegasus.Hook = {
        registrationProductSelection: 'Registration_Product_Selection', // at registration product selection
        orderLookup: 'Order_Lookup', // at order lookup
        claimCreated: 'Claim_Created', // at claim created
        afterReasonSelection: 'After_Reason_Selection', // after reason selection
        beforeClaimSubmission: 'Before_Claim_Submission', // before claim submission
        rmaWebhook: 'RMA_Webhook', // at rma webhook
        returnShipmentWebhook: 'Return_Shipment_Webhook', // at return shipment webhook
        claimFinalized: 'Claim_Finalized', // at claim finalize
        returnShipmentCreated: 'Return_Shipment_Created', // at return shipment purchased
        returnShipmentQuote: 'Return_Shipment_Quote', // at return shipment quote
        beforeClaimLineItemInspection: 'Before_Claim_Line_Item_Inspection', // before claim line item inspection
        orderCheckoutBegin: 'Order_Checkout_Begin', // at order checkout start
        orderCheckoutQuoteShipping: 'Order_Checkout_Quote_Shipping', // at order checkout quote shipping
        themeAppExtensionLoad: 'Theme_App_Extension_Load', // at theme app extension load
        exchangeProductSelection: 'Exchange_Product_Selection', // at variant exchange start
    };
    // TODO evaluate a better way to represent sets/subsets of hooks within a category
    Pegasus.returnOrWarrantyHook = [
        Pegasus.Hook.orderLookup,
        Pegasus.Hook.afterReasonSelection,
        Pegasus.Hook.exchangeProductSelection,
        Pegasus.Hook.returnShipmentQuote,
        Pegasus.Hook.beforeClaimSubmission,
        Pegasus.Hook.claimCreated,
        Pegasus.Hook.returnShipmentCreated,
        Pegasus.Hook.returnShipmentWebhook,
        Pegasus.Hook.rmaWebhook,
        Pegasus.Hook.beforeClaimLineItemInspection,
        Pegasus.Hook.claimFinalized,
    ];
    // ------ fact expressions ---------------------
    Pegasus.factExpression = {
        [Fact.orderCreated]: expression(Fact.orderCreated),
        [Fact.orderCountry]: expression(Fact.orderCountry),
        [Fact.orderTotal]: expression(Fact.orderTotal),
        [Fact.orderTags]: expression(Fact.orderTags),
        [Fact.customerTags]: expression(Fact.customerTags),
        [Fact.rmaStatus]: expression(Fact.rmaStatus),
        [Fact.rmaAllItemsReceived]: expression(Fact.rmaAllItemsReceived),
        [Fact.returnShipmentStatus]: expression(Fact.returnShipmentStatus),
        [Fact.productTags]: expression(Fact.productTags),
        [Fact.productTypes]: expression(Fact.productTypes),
        [Fact.reasonId]: expression(Fact.reasonId),
        [Fact.reasonCategoryCode]: expression(Fact.reasonCategoryCode),
        [Fact.reasonDetailId]: expression(Fact.reasonDetailId),
        [Fact.claimType]: expression(Fact.claimType),
        [Fact.orderNumber]: expression(Fact.orderNumber),
        [Fact.customerEmail]: expression(Fact.customerEmail),
        [Fact.orderChannel]: expression(Fact.orderChannel),
        [Fact.orderProtectedByCorso]: expression(Fact.orderProtectedByCorso),
        [Fact.claimLineItemRequestedResolutionMethod]: expression(Fact.claimLineItemRequestedResolutionMethod),
        [Fact.orderDaysSinceCreated]: expression(Fact.orderDaysSinceCreated),
        [Fact.productCollections]: expression(Fact.productCollections),
        [Fact.exchangeOrderTotal]: expression(Fact.exchangeOrderTotal),
        [Fact.claimLineItemTotal]: expression(Fact.claimLineItemTotal),
        [Fact.statusCustomer]: expression(Fact.statusCustomer),
        [Fact.claimLineItemCustomFieldResponse]: expression(Fact.claimLineItemCustomFieldResponse),
        [Fact.orderGateway]: expression(Fact.orderGateway),
        [Fact.registrationChannel]: expression(Fact.registrationChannel),
        [Fact.registrationEstimatedPurchaseDate]: expression(Fact.registrationEstimatedPurchaseDate),
        [Fact.orderDiscountCode]: expression(Fact.orderDiscountCode),
        [Fact.claimTotal]: expression(Fact.claimTotal),
        [Fact.productVendor]: expression(Fact.productVendor),
        [Fact.productSku]: expression(Fact.productSku),
        [Fact.productGroupId]: expression(Fact.productGroupId),
        [Fact.customerTotalReturnCountLastYear]: expression(Fact.customerTotalReturnCountLastYear),
        [Fact.customerTotalOrderCountAllTime]: expression(Fact.customerTotalOrderCountAllTime),
        [Fact.orderProvince]: expression(Fact.orderProvince),
        [Fact.orderFulfillmentLocationId]: expression(Fact.orderFulfillmentLocationId),
        [Fact.cartDeliveryGroupType]: expression(Fact.cartDeliveryGroupType),
        [Fact.orderLineItemCompareAtPricePercentage]: expression(Fact.orderLineItemCompareAtPricePercentage),
        [Fact.intelligemsTestGroupId]: expression(Fact.intelligemsTestGroupId),
        [Fact.customerSegmentId]: expression(Fact.customerSegmentId),
        [Fact.orderFulfillmentDisplayStatus]: expression(Fact.orderFulfillmentDisplayStatus),
        [Fact.orderCanceled]: expression(Fact.orderCanceled),
        [Fact.orderFinancialStatus]: expression(Fact.orderFinancialStatus),
        [Fact.registrationDaysSincePurchaseEstimate]: expression(Fact.registrationDaysSincePurchaseEstimate),
        [Fact.claimLineItemInspectionGrade]: expression(Fact.claimLineItemInspectionGrade),
        [Fact.returnShipmentLocationId]: expression(Fact.returnShipmentLocationId),
        [Fact.claimLineItemTotalQuantity]: expression(Fact.claimLineItemTotalQuantity),
    };
    // !  WHEN ADDING A FACT, REMEMBER TO PUT IT HERE FOR GOD'S SAKE
    // ! FUCKING KILL ME
    // TODO find a way to enforce that all fact expressions are represented here at compile time; then the related test can be removed
    Pegasus.expressionSchema = z.discriminatedUnion('fact', [
        Pegasus.factExpression[Fact.orderCreated],
        Pegasus.factExpression[Fact.orderCountry],
        Pegasus.factExpression[Fact.orderTotal],
        Pegasus.factExpression[Fact.orderTags],
        Pegasus.factExpression[Fact.customerTags],
        Pegasus.factExpression[Fact.rmaStatus],
        Pegasus.factExpression[Fact.rmaAllItemsReceived],
        Pegasus.factExpression[Fact.returnShipmentStatus],
        Pegasus.factExpression[Fact.productTags],
        Pegasus.factExpression[Fact.productTypes],
        Pegasus.factExpression[Fact.reasonId],
        Pegasus.factExpression[Fact.reasonCategoryCode],
        Pegasus.factExpression[Fact.reasonDetailId],
        Pegasus.factExpression[Fact.claimType],
        Pegasus.factExpression[Fact.orderNumber],
        Pegasus.factExpression[Fact.customerEmail],
        Pegasus.factExpression[Fact.orderChannel],
        Pegasus.factExpression[Fact.orderProtectedByCorso],
        Pegasus.factExpression[Fact.claimLineItemRequestedResolutionMethod],
        Pegasus.factExpression[Fact.exchangeOrderTotal],
        Pegasus.factExpression[Fact.claimLineItemTotal],
        Pegasus.factExpression[Fact.productCollections],
        Pegasus.factExpression[Fact.claimLineItemCustomFieldResponse],
        Pegasus.factExpression[Fact.orderDaysSinceCreated],
        Pegasus.factExpression[Fact.statusCustomer],
        Pegasus.factExpression[Fact.orderGateway],
        Pegasus.factExpression[Fact.registrationEstimatedPurchaseDate],
        Pegasus.factExpression[Fact.registrationChannel],
        Pegasus.factExpression[Fact.orderDiscountCode],
        Pegasus.factExpression[Fact.claimTotal],
        Pegasus.factExpression[Fact.productVendor],
        Pegasus.factExpression[Fact.productSku],
        Pegasus.factExpression[Fact.productGroupId],
        Pegasus.factExpression[Fact.customerTotalReturnCountLastYear],
        Pegasus.factExpression[Fact.customerTotalOrderCountAllTime],
        Pegasus.factExpression[Fact.orderProvince],
        Pegasus.factExpression[Fact.orderFulfillmentLocationId],
        Pegasus.factExpression[Fact.cartDeliveryGroupType],
        Pegasus.factExpression[Fact.orderLineItemCompareAtPricePercentage],
        Pegasus.factExpression[Fact.intelligemsTestGroupId],
        Pegasus.factExpression[Fact.customerSegmentId],
        Pegasus.factExpression[Fact.orderFulfillmentDisplayStatus],
        Pegasus.factExpression[Fact.orderCanceled],
        Pegasus.factExpression[Fact.orderFinancialStatus],
        Pegasus.factExpression[Fact.registrationDaysSincePurchaseEstimate],
        Pegasus.factExpression[Fact.claimLineItemInspectionGrade],
        Pegasus.factExpression[Fact.returnShipmentLocationId],
        Pegasus.factExpression[Fact.claimLineItemTotalQuantity],
    ]);
    const allConditionSchema = z.object({
        all: z.array(Pegasus.expressionSchema),
    });
    const anyConditionSchema = z.object({
        any: z.array(Pegasus.expressionSchema),
    });
    Pegasus.eventClaimFinalizationSettings = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.customizeClaimFinalizationSettings}`),
        params: z.object({
            inventoryBehavior: enumToZodLiteralUnion(InventoryBehavior).optional(),
            failOnOutOfStockScenarios: z.boolean(),
            failureToFinalizeNotificationEmails: helperSchema.emailList.optional(),
        }),
    });
    Pegasus.eventAllowCustomerToKeepItem = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.allowCustomerToKeepItem}`),
        params: z.object({
        // ? we might need to add a message to display after a yes selection
        }),
    });
    // ------------ policy enforcement question ------------
    const markAsIneligible = z.literal('markAsIneligible');
    Pegasus.eventPolicyEnforcement = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.askAPolicyEnforcingQuestion}`),
        params: z.object({
            question: helperSchema.nonEmptyString,
            // ? optional message to display after a yes selection
            onYesMessage: z.string().optional(),
            yes: markAsIneligible,
            no: z.null(), // ! do nothing
        }),
    });
    Pegasus.eventAllowClaimTypeSelection = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.allowClaimTypeSelection}`),
        params: z.object({
            claimTypeSelectionDetailText: helperSchema.nonEmptyString,
        }),
    });
    const tagWithoutId = z.object({
        name: z.string(),
    });
    Pegasus.eventApplyClaimTags = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.applyClaimTags}`),
        params: z.object({
            claimTags: z.array(tagWithoutId),
        }),
    });
    Pegasus.eventApplyOrderTags = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.applyOrderTags}`),
        params: z.object({
            orderTagAction: z.union([
                z.literal('Replacement Order'),
                z.literal('Original Order'),
            ]),
            orderTags: z.array(tagWithoutId),
            tagWithReason: z.boolean().optional(),
        }),
    });
    // auto finalize claim
    const allowedResolutionMethodOptions = z.array(z.union([
        z.literal(`${CrewClaimResolutionMethodEnum.refund}`),
        z.literal(`${CrewClaimResolutionMethodEnum.giftCard}`),
        z.literal(`${CrewClaimResolutionMethodEnum.variantExchange}`),
    ]));
    Pegasus.eventAutoFinalizeClaim = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.autoFinalizeClaim}`),
        params: z.object({
            // todo: remove this after fully moving off it in favor of conditions
            allowedResolutionMethods: allowedResolutionMethodOptions.optional(),
        }),
    });
    const ineligibleLineItemFootnoteSchema = optionalize(z
        .string()
        .max(30, {
        message: 'Footnote must be less than 30 characters',
    })
        .trim());
    const eligibilityDaysSchema = helperSchema.policyDays.nullish();
    Pegasus.eventModifyResolutionWindow = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.modifyResolutionWindow}`),
        // might need to XOR dates so that at least one is required; otherwise, this wouldn't do anything if nothing was specified
        params: z.discriminatedUnion('kind', [
            z.object({
                kind: z.literal('returnableUntil'),
                ineligibleLineItemFootnote: ineligibleLineItemFootnoteSchema,
                refund: helperSchema.optionalizedDatetime,
                giftCard: helperSchema.optionalizedDatetime,
                exchange: helperSchema.optionalizedDatetime, // ? should this be `variantExchange`
                warrantyReview: helperSchema.optionalizedDatetime,
            }),
            z.object({
                kind: z.literal('eligibilityDays'),
                ineligibleLineItemFootnote: ineligibleLineItemFootnoteSchema,
                refund: eligibilityDaysSchema,
                giftCard: eligibilityDaysSchema,
                exchange: eligibilityDaysSchema, // ? should this be `variantExchange`
                warrantyReview: eligibilityDaysSchema,
            }),
        ]),
    });
    Pegasus.eventCollectCustomFieldsParams = z.object({
        customFieldIds: z.array(z.number()),
        showDefaultCommentField: z.boolean(),
        isDefaultCommentFieldRequired: z.boolean().optional(),
        isMediaUploadRequired: z.boolean(),
        mediaUploadInstructions: z.string().optional(),
        minimumMediaUploadAmount: z.number().optional(),
    });
    Pegasus.eventCollectCustomFields = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.collectCustomFields}`),
        params: Pegasus.eventCollectCustomFieldsParams,
    });
    Pegasus.eventCustomizeClaimLineItemInspection = z.object({
        type: z.literal(`${Pegasus.EventType.customizeClaimLineItemInspection}`),
        storeRuleId: z.number().optional(),
        params: z.object({
            inspectionCustomFieldIds: z.array(z.number()),
            showInspectionComment: z.boolean(),
            showInspectionMediaUpload: z.boolean(),
            shouldCollectInspectionGrade: z.boolean(),
            inspectionInstructions: helperSchema.optionalizedString,
        }),
    });
    // ? possibly elevate into zod extensions
    function shouldModify(schema) {
        return z.discriminatedUnion('shouldModify', [
            z.object({ shouldModify: z.literal(false) }),
            schema.merge(z.object({ shouldModify: z.literal(true) })),
        ]);
    }
    Pegasus.shouldModify = shouldModify;
    Pegasus.eventModifyIncentives = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.modifyIncentives}`),
        params: z.object({
            giftCardIncentive: shouldModify(z.object({
                amount: helperSchema.nonNegativeNumber,
                type: enumToZodLiteralUnion(CrewIncentiveTypeEnum),
            })),
        }),
    });
    Pegasus.eventChargeForReturnLabel = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.chargeForReturnLabel}`),
        params: z.object({
            applyLabelMarkup: shouldModify(z.object({
                amount: helperSchema.nonNegativeNumber,
                type: enumToZodLiteralUnion(CrewIncentiveTypeEnum),
            })),
        }),
    });
    Pegasus.eventProductRegistrationSelection = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.productRegistrationSelection}`),
        params: z.object({
            productRegistrationProductGroupId: z.coerce
                .number()
                .gt(0, 'Please select a Product Group'),
        }),
    });
    Pegasus.eventReturnShippingConfig = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.returnShippingConfig}`),
        params: z.object({
            returnMethods: z
                .array(z.union([z.literal('Label'), z.literal('Packing_Slip')]))
                .min(1, 'Please select at least one Return Method'),
            returnLocationId: z.coerce
                .number()
                .gt(0, 'Please select a Return Location'),
        }),
    });
    Pegasus.eventChargeForExchangeOrderShipping = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.chargeForExchangeOrderShipping}`),
        params: z.object({
            applyExchangeOrderShippingMarkup: shouldModify(z.object({
                amount: helperSchema.nonNegativeNumber,
                type: enumToZodLiteralUnion(CrewIncentiveTypeEnum),
            })),
        }),
    });
    Pegasus.eventApplyFee = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.applyFee}`),
        params: z.object({
            feeDisplayName: z.string(),
            feeAmount: z.number().nonnegative(),
            feeType: enumToZodLiteralUnion(CrewIncentiveTypeEnum).default('Fixed'),
        }),
    });
    // ? we might need to add a message to display after a yes selection
    // ? should we allow incentives here too?
    // ? should we allow conditions here too?
    Pegasus.eventShopNow = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.shopNow}`),
        params: z.object({
            startShoppingUrl: z
                .string()
                .url()
                .min(1, 'Start Shopping URL is required'),
            shopNowHeadline: helperSchema.nonEmptyString,
            shopNowDescription: helperSchema.nonEmptyString,
            shopNowIncentive: shouldModify(z.object({
                amount: helperSchema.nonNegativeNumber,
                type: enumToZodLiteralUnion(CrewIncentiveTypeEnum),
            })).optional(),
        }),
    });
    Pegasus.eventOfferInstantExchangeParams = z.object({
        timeToReturnBeforeCharging: z.literal(7), // ! this can be altered once we determine how Stripe interacts with us for pre-authorizations
        isInstantExchangeRequired: z.boolean().default(false),
    });
    Pegasus.eventOfferInstantExchange = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.offerInstantExchange}`),
        params: Pegasus.eventOfferInstantExchangeParams,
    });
    Pegasus.eventQuoteOrderShipment = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.quoteOrderShipment}`),
        params: z.object({
            storeShipRateIds: z.array(z.number()),
        }),
    });
    // ------------- quote order shipment modify ---------------------
    Pegasus.QuoteModification = {
        APPLY_FIXED_FEE: 'APPLY_FIXED_FEE',
        APPLY_PERCENTAGE_FEE: 'APPLY_PERCENTAGE_FEE',
        ADD_OR_REMOVE_RATES: 'ADD_OR_REMOVE_RATES',
        APPLY_SHIPPING_DISCOUNT: 'APPLY_SHIPPING_DISCOUNT',
    };
    Pegasus.USHoliday = {
        NEW_YEARS_DAY: 'NEW_YEARS_DAY',
        MARTIN_LUTHER_KING_JR_DAY: 'MARTIN_LUTHER_KING_JR_DAY',
        PRESIDENTS_DAY: 'PRESIDENTS_DAY',
        MEMORIAL_DAY: 'MEMORIAL_DAY',
        INDEPENDENCE_DAY: 'INDEPENDENCE_DAY',
        LABOR_DAY: 'LABOR_DAY',
        VETERANS_DAY: 'VETERANS_DAY',
        THANKSGIVING_DAY: 'THANKSGIVING_DAY',
        CHRISTMAS_DAY: 'CHRISTMAS_DAY',
        INDIGENOUS_PEOPLES_DAY: 'INDIGENOUS_PEOPLES_DAY',
        JUNETEENTH: 'JUNETEENTH',
    };
    Pegasus.estimatedDeliveryDisplayLocationUnion = z.union([
        z.literal('EXTENSION'),
        z.literal('ADD_TO_DESCRIPTION'),
    ]);
    Pegasus.EstimatedDeliveryStrategy = {
        STATIC: 'STATIC',
        CARRIER_PROVIDED: 'CARRIER_PROVIDED',
    };
    // ! When updating, changing the delivery estimate ensure that
    // ! the changes are reflected in packages/corso-types/corso-shopify-extensions/shared.ts
    Pegasus.provideEstimatedDeliveryDate = z.object({
        strategy: enumToZodLiteralUnion(Pegasus.EstimatedDeliveryStrategy),
        estimatedDeliveryShippingCutOffTime: z.coerce.number().optional(),
        estimatedDeliveryRangeStartDays: z.number().nonnegative().default(3),
        estimatedDeliveryRangeEndDays: z.number().nonnegative().optional(),
        estimatedDeliveryDisplayLocation: Pegasus.estimatedDeliveryDisplayLocationUnion,
        estimatedDeliveryFulfillmentTz: z.string().optional(),
        carrierEstimatedFulfillmentDays: z.number().nonnegative().optional(),
        estimatedDeliveryHolidays: z
            .array(enumToZodLiteralUnion(Pegasus.USHoliday))
            .optional()
            .default(Object.values(Pegasus.USHoliday)),
        estimatedTargetStoreShipRateIds: z.array(z.number()),
    });
    const applyFixedFee = z.object({
        kind: z.literal(Pegasus.QuoteModification.APPLY_FIXED_FEE),
        amount: z.number().nonnegative(),
        applyPerItem: z.boolean().default(false),
        fixedFeeStoreShipRateIds: z.array(z.number()).optional().default([]),
    });
    const applyPercentageFee = z.object({
        kind: z.literal(Pegasus.QuoteModification.APPLY_PERCENTAGE_FEE),
        percentage: z.number().nonnegative(),
        percentageFeeStoreShipRateIds: z.array(z.number()).optional().default([]),
    });
    const actionUnion = z.union([
        z.literal('ADD'),
        z.literal('REMOVE'),
        z.literal('REPLACE'),
    ]);
    const addOrRemoveRates = z.object({
        kind: z.literal(Pegasus.QuoteModification.ADD_OR_REMOVE_RATES),
        action: actionUnion,
        addOrRemoveStoreShipRateIds: z.array(z.number()),
    });
    Pegasus.ShippingDiscountType = {
        percentage: 'percentage',
        fixedAmount: 'fixedAmount',
        removePlusPricing: 'removePlusPricing',
    };
    Pegasus.shippingDiscount = z.object({
        kind: z.literal(Pegasus.QuoteModification.APPLY_SHIPPING_DISCOUNT),
        discountMessage: z.string().optional(),
        discountType: enumToZodLiteralUnion(Pegasus.ShippingDiscountType),
        discountAmount: z.number().nonnegative(),
        discountStoreShipRateIds: z.array(z.number()).optional().default([]),
        isCheckboxApplicationEnabled: z.boolean().optional().default(true),
    });
    const modifications = z.discriminatedUnion('kind', [
        applyPercentageFee,
        applyFixedFee,
        addOrRemoveRates,
        Pegasus.shippingDiscount,
    ]);
    Pegasus.eventQuoteOrderShipmentModify = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.quoteOrderShipmentModify}`),
        params: z.object({
            checkoutRateModifications: z.array(modifications),
        }),
    });
    Pegasus.eventQuoteOrderShipmentDeliveryEstimate = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.quoteOrderShipmentDeliveryEstimate}`),
        params: z.object({
            deliveryEstimateConfigs: z.array(Pegasus.provideEstimatedDeliveryDate),
        }),
    });
    // ------------- restock inventory ---------------------
    Pegasus.RestockPlatform = {
        SHOPIFY: 'SHOPIFY',
        XORO: 'XORO',
    };
    const baseRestockInventoryEventParams = z.object({
        restockPlatform: enumToZodLiteralUnion(Pegasus.RestockPlatform),
    });
    Pegasus.RestockName = {
        available: 'available',
        damaged: 'damaged',
        none: 'none',
    };
    const shopifyRestockInventoryEventParams = baseRestockInventoryEventParams.extend({
        restockPlatform: z.literal(Pegasus.RestockPlatform.SHOPIFY),
        restockLocationIdFromPlatform: z.string().optional(),
        shopifyRestockName: enumToZodLiteralUnion(Pegasus.RestockName),
    });
    const zoroRestockInventoryEventParams = baseRestockInventoryEventParams.extend({
        restockPlatform: z.literal(Pegasus.RestockPlatform.XORO),
    });
    Pegasus.eventRestockInventory = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.restockInventory}`),
        params: z.discriminatedUnion('restockPlatform', [
            shopifyRestockInventoryEventParams,
            zoroRestockInventoryEventParams,
        ]),
    });
    // ------------- order edit ---------------------
    Pegasus.OrderEditType = {
        CANCEL_ORDER: 'CANCEL_ORDER',
        UPDATE_SHIPPING_ADDRESS: 'UPDATE_SHIPPING_ADDRESS',
    };
    Pegasus.orderEditTypeUnion = z.union([
        z.literal(`${Pegasus.OrderEditType.CANCEL_ORDER}`),
        z.literal(`${Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS}`),
    ]);
    const baseOrderEditEventParams = z.object({
        storeRuleId: z.number().optional(),
        editStartLabel: z.string().optional(),
        editConfirmLabel: z.string().optional(),
        editSuccessLabel: z.string().optional(),
    });
    Pegasus.orderEditCancelOrderActionSchema = baseOrderEditEventParams.extend({
        editType: z.literal(`${Pegasus.OrderEditType.CANCEL_ORDER}`),
        shouldNotifyOnCancellation: z.boolean().default(true),
        shouldRestockOnCancellation: z.boolean().default(true),
    });
    Pegasus.orderEditUpdateAddressActionSchema = baseOrderEditEventParams.extend({
        editType: z.literal(`${Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS}`),
    });
    Pegasus.orderEditAction = z.discriminatedUnion('editType', [
        Pegasus.orderEditCancelOrderActionSchema,
        Pegasus.orderEditUpdateAddressActionSchema,
    ]);
    Pegasus.eventOrderEditing = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.orderEditing}`),
        params: Pegasus.orderEditAction,
    });
    Pegasus.AdvancedExchangeMethod = {
        PAYMENT: 'PAYMENT', // Previously UPSELL
        PAYMENT_AND_PARTIAL_REFUND: 'PAYMENT_AND_PARTIAL_REFUND', // Previously PARTIAL_REFUND
        NO_PAYMENT_OR_REFUND: 'NO_PAYMENT_OR_REFUND', // Previously NO_PAYMENT
    };
    Pegasus.advancedExchangeMethodUnion = z.union([
        z.literal(`${Pegasus.AdvancedExchangeMethod.PAYMENT}`),
        z.literal(`${Pegasus.AdvancedExchangeMethod.PAYMENT_AND_PARTIAL_REFUND}`),
        z.literal(`${Pegasus.AdvancedExchangeMethod.NO_PAYMENT_OR_REFUND}`),
    ]);
    Pegasus.AdvancedExchangeSortCriteria = {
        ALPHABETICAL: 'ALPHABETICAL',
        NEWEST_FIRST: 'NEWEST_FIRST',
    };
    const advancedExchangeSortOptionUnion = z.union([
        z.literal(`${Pegasus.AdvancedExchangeSortCriteria.ALPHABETICAL}`),
        z.literal(`${Pegasus.AdvancedExchangeSortCriteria.NEWEST_FIRST}`),
    ]);
    const baseParamsOfferAdvancedExchange = z.object({
        isAdvancedExchangeFilterAndSearchEnabled: z
            .boolean()
            .default(true)
            .optional(),
        advancedExchangeSortCriteria: advancedExchangeSortOptionUnion.default(`${Pegasus.AdvancedExchangeSortCriteria.ALPHABETICAL}`),
        excludedAdvancedExchangeOptionNames: z.array(z.string()).default(['Title']),
        minInventoryAvailableToCustomer: helperSchema.nonNegativeNumber.nullish(),
        advancedExchangeMethod: Pegasus.advancedExchangeMethodUnion.default('NO_PAYMENT_OR_REFUND'),
    });
    Pegasus.eventOfferAdvancedExchange = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.offerAdvancedExchange}`),
        params: z.discriminatedUnion('exchangeKind', [
            baseParamsOfferAdvancedExchange.extend({
                exchangeKind: z.literal('product'),
                storeProductGroupIds: z
                    .array(z.number())
                    .min(1, 'Please select at least one Product Group'),
            }),
            baseParamsOfferAdvancedExchange.extend({
                exchangeKind: z.literal('variant'),
            }),
        ]),
    });
    const parcel = z.object({
        name: z.string(),
        length: z.number().nonnegative().min(1),
        width: z.number().nonnegative().min(1),
        height: z.number().nonnegative().min(1),
    });
    Pegasus.eventQuoteOrderShipmentParcel = z.object({
        storeRuleId: z.number().optional(),
        type: z.literal(`${Pegasus.EventType.quoteOrderShipmentParcel}`),
        params: z.object({
            parcel,
        }),
    });
    Pegasus.eventThemeAppExtensionLoad = z.object({
        type: z.literal(`${Pegasus.EventType.themeAppExtensionLoad}`),
        storeRuleId: z.number().optional(),
        // ? Someday this could be modeled like eventQuoteOrderShipmentModify and provide
        // ? A bunch of different configs
        params: z.object({
            themeAppConfigs: z
                .array(Pegasus.provideEstimatedDeliveryDate.omit({
                estimatedDeliveryDisplayLocation: true,
                estimatedTargetStoreShipRateIds: true,
            }))
                .max(1),
        }),
    });
    // ----------- type relationship enforcement --------------
    Pegasus.hooksForEvent = {
        // order lookup
        [Pegasus.EventType.orderEditing]: [Pegasus.Hook.orderLookup],
        [Pegasus.EventType.modifyResolutionWindow]: [Pegasus.Hook.orderLookup],
        [Pegasus.EventType.offerInstantExchange]: [Pegasus.Hook.orderLookup],
        [Pegasus.EventType.allowClaimTypeSelection]: [Pegasus.Hook.orderLookup],
        // after reason selection
        [Pegasus.EventType.askAPolicyEnforcingQuestion]: [Pegasus.Hook.afterReasonSelection],
        [Pegasus.EventType.collectCustomFields]: [Pegasus.Hook.afterReasonSelection],
        // exchange product selection
        [Pegasus.EventType.offerAdvancedExchange]: [Pegasus.Hook.exchangeProductSelection],
        // return shipment quote
        [Pegasus.EventType.returnShippingConfig]: [Pegasus.Hook.returnShipmentQuote],
        // before claim submission
        [Pegasus.EventType.chargeForReturnLabel]: [Pegasus.Hook.beforeClaimSubmission],
        [Pegasus.EventType.chargeForExchangeOrderShipping]: [Pegasus.Hook.beforeClaimSubmission],
        [Pegasus.EventType.modifyIncentives]: [Pegasus.Hook.beforeClaimSubmission],
        [Pegasus.EventType.applyFee]: [Pegasus.Hook.beforeClaimSubmission],
        // claim created
        [Pegasus.EventType.applyClaimTags]: [
            Pegasus.Hook.claimCreated,
            Pegasus.Hook.rmaWebhook,
            Pegasus.Hook.returnShipmentWebhook,
            Pegasus.Hook.returnShipmentCreated,
        ],
        [Pegasus.EventType.autoFinalizeClaim]: [
            Pegasus.Hook.rmaWebhook,
            Pegasus.Hook.returnShipmentWebhook,
        ],
        [Pegasus.EventType.allowCustomerToKeepItem]: [Pegasus.Hook.returnShipmentCreated],
        [Pegasus.EventType.customizeClaimLineItemInspection]: [
            Pegasus.Hook.beforeClaimLineItemInspection,
        ],
        [Pegasus.EventType.applyOrderTags]: [Pegasus.Hook.claimCreated, Pegasus.Hook.claimFinalized],
        [Pegasus.EventType.customizeClaimFinalizationSettings]: [Pegasus.Hook.claimFinalized],
        [Pegasus.EventType.shopNow]: [Pegasus.Hook.orderLookup],
        // checkout + theme app + registration
        [Pegasus.EventType.productRegistrationSelection]: [
            Pegasus.Hook.registrationProductSelection,
        ],
        [Pegasus.EventType.restockInventory]: [Pegasus.Hook.claimFinalized],
        [Pegasus.EventType.quoteOrderShipment]: [Pegasus.Hook.orderCheckoutBegin],
        [Pegasus.EventType.quoteOrderShipmentModify]: [Pegasus.Hook.orderCheckoutQuoteShipping],
        [Pegasus.EventType.quoteOrderShipmentParcel]: [Pegasus.Hook.orderCheckoutQuoteShipping],
        [Pegasus.EventType.quoteOrderShipmentDeliveryEstimate]: [
            Pegasus.Hook.orderCheckoutQuoteShipping,
        ],
        [Pegasus.EventType.themeAppExtensionLoad]: [Pegasus.Hook.themeAppExtensionLoad],
    };
    Pegasus.eventsForHook = Object.values(Pegasus.Hook).reduce((efh, hook) => {
        // eslint-disable-next-line no-param-reassign
        efh[hook] = Object.values(Pegasus.EventType).filter((event) => {
            const potentialHooks = Pegasus.hooksForEvent[event]; // TODO add @total-typescript/ts-reset for everything
            return potentialHooks.includes(hook);
        });
        return efh;
    }, {}); // TODO see if we can improve/eliminate this cast
    Pegasus.registrationFacts = [
        Fact.registrationEstimatedPurchaseDate,
        Fact.registrationChannel,
        Fact.registrationDaysSincePurchaseEstimate,
    ];
    Pegasus.orderFacts = [
        Fact.orderFinancialStatus,
        Fact.orderDiscountCode,
        Fact.orderCreated,
        Fact.orderCountry,
        Fact.orderProvince,
        Fact.orderGateway,
        Fact.orderTotal,
        Fact.orderTags,
        Fact.customerTags,
        Fact.orderNumber,
        Fact.customerEmail,
        Fact.orderChannel,
        Fact.orderProtectedByCorso,
        Fact.orderDaysSinceCreated,
        Fact.statusCustomer,
        Fact.customerTotalOrderCountAllTime,
        Fact.orderFulfillmentDisplayStatus,
        Fact.orderCanceled,
        ...Pegasus.registrationFacts,
    ];
    Pegasus.lineItemFacts = [
        Fact.productTags,
        Fact.productTypes,
        Fact.productCollections,
        Fact.productVendor,
        Fact.productSku,
        Fact.productGroupId,
        Fact.orderLineItemCompareAtPricePercentage,
    ];
    Pegasus.claimLineItemFacts = [
        Fact.reasonId,
        Fact.reasonCategoryCode,
        Fact.reasonDetailId,
        Fact.claimLineItemRequestedResolutionMethod,
        Fact.claimLineItemInspectionGrade,
        Fact.claimLineItemTotalQuantity,
        Fact.returnShipmentLocationId,
        ...Pegasus.lineItemFacts,
    ];
    const claimFacts = [Fact.claimType, Fact.claimTotal];
    Pegasus.factsForHook = {
        [Pegasus.Hook.returnShipmentQuote]: [
            ...Pegasus.orderFacts,
            ...Pegasus.claimLineItemFacts,
            ...claimFacts,
            Fact.exchangeOrderTotal,
        ],
        [Pegasus.Hook.orderLookup]: [...Pegasus.orderFacts, ...Pegasus.lineItemFacts],
        [Pegasus.Hook.afterReasonSelection]: [
            ...Pegasus.orderFacts,
            ...Pegasus.claimLineItemFacts,
            ...claimFacts,
        ],
        [Pegasus.Hook.beforeClaimSubmission]: [
            ...Pegasus.orderFacts,
            ...Pegasus.claimLineItemFacts,
            ...claimFacts,
            Fact.exchangeOrderTotal,
        ],
        [Pegasus.Hook.returnShipmentCreated]: [
            ...Pegasus.orderFacts,
            ...Pegasus.claimLineItemFacts,
            Fact.claimLineItemTotal,
            Fact.claimLineItemCustomFieldResponse,
            ...claimFacts,
        ],
        [Pegasus.Hook.orderCheckoutBegin]: [Fact.orderCountry, Fact.orderProvince],
        [Pegasus.Hook.claimCreated]: [
            ...Pegasus.orderFacts,
            ...Pegasus.claimLineItemFacts,
            ...claimFacts,
            Fact.claimLineItemCustomFieldResponse,
        ],
        [Pegasus.Hook.beforeClaimLineItemInspection]: [
            ...Pegasus.orderFacts,
            ...Pegasus.claimLineItemFacts,
            Fact.claimType,
            Fact.claimLineItemCustomFieldResponse,
        ],
        [Pegasus.Hook.returnShipmentWebhook]: [
            ...Pegasus.orderFacts,
            ...Pegasus.claimLineItemFacts,
            ...claimFacts,
            Fact.customerTotalReturnCountLastYear,
            Fact.returnShipmentStatus,
            Fact.claimLineItemCustomFieldResponse,
        ],
        [Pegasus.Hook.rmaWebhook]: [
            ...Pegasus.orderFacts,
            ...Pegasus.claimLineItemFacts,
            ...claimFacts,
            Fact.rmaStatus,
            Fact.rmaAllItemsReceived,
            Fact.claimLineItemCustomFieldResponse,
        ],
        [Pegasus.Hook.claimFinalized]: [
            ...Pegasus.orderFacts,
            ...Pegasus.claimLineItemFacts,
            ...claimFacts,
        ],
        [Pegasus.Hook.registrationProductSelection]: Pegasus.registrationFacts,
        [Pegasus.Hook.orderCheckoutQuoteShipping]: [
            Fact.productGroupId,
            Fact.customerTags,
            Fact.orderFulfillmentLocationId,
            Fact.cartDeliveryGroupType,
            Fact.intelligemsTestGroupId,
            Fact.productSku,
            Fact.productVendor,
            Fact.orderCountry,
            Fact.orderProvince,
            Fact.customerSegmentId,
        ],
        [Pegasus.Hook.themeAppExtensionLoad]: [Fact.orderCountry],
        [Pegasus.Hook.exchangeProductSelection]: [Fact.productGroupId],
    };
    /** Derived from Zod schema for fact expressions. */
    function operatorForFact(fact) {
        const valueType = Pegasus.factValueType[fact];
        return Pegasus.valueOperators[valueType];
    }
    Pegasus.operatorForFact = operatorForFact;
    // factExpression[fact].shape.operator.options.map((option) => option.value);
    /**
     * Derived from Zod schema for fact expressions.
     * Given a fact, it's definition should handle the relationship between the fact, operator, and value.
     * As some point these might need to become independent, where a fact defines shape of it's value, and the operators available are enforced by the resulting expressions.
     */
    function valueSchemaForFact(fact) {
        const valueType = Pegasus.factValueType[fact];
        return Pegasus.valueTypeSchema[valueType];
    }
    Pegasus.valueSchemaForFact = valueSchemaForFact;
    // ----------- event + rule schema ------------
    // todo: enforce all event schemas
    Pegasus.eventSchema = z.union([
        Pegasus.eventOrderEditing,
        Pegasus.eventOfferAdvancedExchange,
        Pegasus.eventModifyResolutionWindow,
        Pegasus.eventOfferInstantExchange,
        Pegasus.eventApplyClaimTags,
        Pegasus.eventAllowClaimTypeSelection,
        Pegasus.eventCollectCustomFields,
        Pegasus.eventAutoFinalizeClaim,
        Pegasus.eventPolicyEnforcement,
        Pegasus.eventClaimFinalizationSettings,
        Pegasus.eventApplyFee,
        Pegasus.eventModifyIncentives,
        Pegasus.eventChargeForReturnLabel,
        Pegasus.eventChargeForExchangeOrderShipping,
        Pegasus.eventAllowCustomerToKeepItem,
        Pegasus.eventProductRegistrationSelection,
        Pegasus.eventReturnShippingConfig,
        Pegasus.eventApplyOrderTags,
        Pegasus.eventCustomizeClaimLineItemInspection,
        Pegasus.eventQuoteOrderShipment,
        Pegasus.eventQuoteOrderShipmentModify,
        Pegasus.eventThemeAppExtensionLoad,
        Pegasus.eventQuoteOrderShipmentParcel,
        Pegasus.eventQuoteOrderShipmentDeliveryEstimate,
        Pegasus.eventRestockInventory,
        Pegasus.eventShopNow,
    ]);
    Pegasus.eventsSchemaArray = z.array(Pegasus.eventSchema);
    Pegasus.ruleSchema = z.object({
        priority: z
            .number({ invalid_type_error: 'Must be a number' })
            .positive({ message: 'Must be greater than zero' })
            .int({ message: 'Must be a whole number' }), // ! 1 is highest priority, with decreasing priority as the number increases
        conditions: z.union([allConditionSchema, anyConditionSchema]),
        event: Pegasus.eventSchema,
        storeRuleId: z.number().optional(),
    });
})(Pegasus || (Pegasus = {}));
//# sourceMappingURL=pegasus.js.map